import { useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { otpFrom } from '@/utils/constants';
import { useGetErrorMessage } from '@/utils/dbMessagesHelper';
import { FiArrowLeft } from 'react-icons/fi';
import { toast } from 'sonner';

import { email_template_config } from '@/config/email_template.config';
import useOtpSend from '@/hooks/useOtpSend';
import { useTranslations } from '@/hooks/useTranslations';
import { Button } from '@/components/ui/button';
import OtpInput from '@/components/reUsables/OtpInput';

const OTPForm = ({
  loading,
  email,
  value,
  setValue,
  handleVerify,
  setIsEmailForm,
  url,
  fromSignup,
  tenantId,
  errorMsg,
  setErrorMsg,
}: {
  loading: boolean;
  email: string;
  value: string;
  setValue: (value: string) => void;
  handleVerify: () => void;
  setIsEmailForm?: (isEmailForm: boolean) => void;
  url?: string;
  fromSignup?: boolean;
  tenantId: string;
  handleRevertBackToEmailForm?: () => void;
  errorMsg?: string;
  setErrorMsg: any;
}) => {
  const { translations, isLoading: isTranslationsLoading } =
    useTranslations('auth_page');

  const { getDBMessage } = useGetErrorMessage();

  const enter_the_verification_otp =
    translations?.enter_the_verification_otp ||
    'Enter the verification OTP sent to';
  const verification_code =
    translations?.verification_code || 'Verification code';
  const clear = translations?.clear || 'Clear';
  const didnt_receive_otp =
    translations?.didnt_receive_otp || "Didn't receive OTP?";
  const send_again = translations?.send_again || 'Send again';
  const sending = translations?.sending || 'Sending...';
  const verify_otp = translations?.verify_otp || 'Verify OTP';
  const verifying = translations?.verifying || 'Verifying...';
  const have_an_account = translations?.have_an_account || 'Have an account?';
  const login = translations?.login || 'Login';
  const login_with_another_account =
    translations?.login_with_another_account || 'Login with another account?';
  const signup_with_another_email =
    translations?.signup_with_another_email || 'Signup with another email';

  const {
    loading: otpSendLoading,
    data,
    handleClick,
  } = useOtpSend(
    fromSignup
      ? email_template_config.SIGNUP_OTP_TEMPLATE
      : email_template_config.SIGNIN_OTP_TEMPLATE
  );
  const router = useRouter();

  useEffect(() => {
    if (data?.message) {
      setValue('');
      toast.success(getDBMessage(data?.message) || data?.message);
    }
  }, [data]);

  useEffect(() => {
    if (value?.length <= 0 && errorMsg) {
      setErrorMsg('');
    }
  }, [value, errorMsg]);

  if (isTranslationsLoading) {
    return (
      <div className="mt-8 space-y-4">
        <div className="h-4 w-64 animate-pulse rounded bg-gray-200" />
        <div className="space-y-2">
          <div className="h-10 w-full animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
          <div className="h-10 w-full animate-pulse rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <p className="text-sm text-zinc-500">
        {enter_the_verification_otp}{' '}
        <span className="text-zinc-950">{email} </span>
      </p>
      <div className="mt-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-medium text-zinc-950">
            {verification_code}
          </p>
          {value && (
            <div
              className="cursor-pointer text-sm font-medium text-zinc-950"
              onClick={() => {
                setValue('');
                setErrorMsg('');
              }}
            >
              {clear}
            </div>
          )}
        </div>

        <OtpInput value={value} setValue={setValue} isError={errorMsg} />

        <p className="mt-5 text-sm text-zinc-500">
          {didnt_receive_otp}{' '}
          <span
            onClick={() => {
              if (otpSendLoading) {
                console.log(loading);
              } else {
                setErrorMsg('');
                setValue('');
                handleClick(email, tenantId, otpFrom.SIGNUP, fromSignup);
              }
            }}
            className={` font-medium ${otpSendLoading ? 'cursor-wait text-zinc-400' : 'cursor-pointer text-primary-950'} `}
          >
            {otpSendLoading ? sending : send_again}
          </span>{' '}
        </p>
        <br />
        <Button
          disabled={loading || otpSendLoading}
          onClick={handleVerify}
          type="button"
          size="fullWidth"
        >
          {loading ? verifying : verify_otp}
        </Button>
      </div>
      {fromSignup ? (
        <>
          <p className="pt-6 text-center text-sm text-zinc-500">
            {have_an_account}{' '}
            <Link
              className="cursor-pointer font-medium text-primary-950"
              href="/"
            >
              {login}
            </Link>
          </p>

          <div
            onClick={() => window.location.reload()}
            className="mt-6 flex cursor-pointer items-center justify-center gap-2 text-primary-950"
          >
            <FiArrowLeft className="h-5 w-5" />
            <p className="text-sm font-medium">{signup_with_another_email}</p>
          </div>
        </>
      ) : (
        <p
          onClick={() =>
            url ? router.push(url) : setIsEmailForm && setIsEmailForm(true)
          }
          className="cursor-pointer pt-6 text-center text-sm font-medium text-primary-950 hover:underline"
        >
          {login_with_another_account}
        </p>
      )}
    </div>
  );
};

export default OTPForm;
